'use client';

import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { createContext, useContext, useEffect, useRef, ReactNode } from 'react';

interface AmplitudeContextType {
  track: (eventName: string, eventProperties?: Record<string, unknown>) => void;
  identify: (userId: string, userProperties?: Record<string, unknown>) => void;
  setUserId: (userId: string) => void;
}

const AmplitudeContext = createContext<AmplitudeContextType | undefined>(undefined);

interface AmplitudeProviderProps {
  children: ReactNode;
}

export default function AmplitudeProvider({ children }: AmplitudeProviderProps) {
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && typeof window !== 'undefined') {
      // Add Session Replay plugin
      amplitude.add(sessionReplayPlugin({sampleRate: 1}));
      
      // Initialize amplitude with the provided configuration
      amplitude.init('4f3ee42a2955079965dd4cb884e8a60b', {
        "autocapture": true,
        "serverZone": "EU"
      });
      
      initialized.current = true;
    }
  }, []);

  const amplitudeTrack = (eventName: string, eventProperties?: Record<string, unknown>) => {
    if (initialized.current && typeof window !== 'undefined') {
      amplitude.track(eventName, eventProperties);
    }
  };

  const amplitudeIdentify = (userId: string, userProperties?: Record<string, unknown>) => {
    if (initialized.current && typeof window !== 'undefined') {
      amplitude.setUserId(userId);
      if (userProperties) {
        const identifyEvent = new amplitude.Identify();
        Object.entries(userProperties).forEach(([key, value]) => {
          if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            identifyEvent.set(key, value);
          }
        });
        amplitude.identify(identifyEvent);
      }
    }
  };

  const amplitudeSetUserId = (userId: string) => {
    if (initialized.current && typeof window !== 'undefined') {
      amplitude.setUserId(userId);
    }
  };

  const contextValue: AmplitudeContextType = {
    track: amplitudeTrack,
    identify: amplitudeIdentify,
    setUserId: amplitudeSetUserId,
  };

  return (
    <AmplitudeContext.Provider value={contextValue}>
      {children}
    </AmplitudeContext.Provider>
  );
}

export const useAmplitude = () => {
  const context = useContext(AmplitudeContext);
  if (context === undefined) {
    throw new Error('useAmplitude must be used within an AmplitudeProvider');
  }
  return context;
};